import { create } from 'zustand';

// Central store for the user's reusable personal data: senders, cities,
// recipients and signatures. Each collection is a list; `defaults` keeps
// the id of the last-used entry of each kind so it can be pre-selected.
const KEY = 'pdf-tools.account.v1';

// Collection kinds (also the state keys).
export const KINDS = ['senders', 'cities', 'recipients', 'signatures', 'attachments'];

function load() {
  try {
    const raw = localStorage.getItem(KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

function persist(s) {
  try {
    localStorage.setItem(
      KEY,
      JSON.stringify({
        senders: s.senders,
        cities: s.cities,
        recipients: s.recipients,
        signatures: s.signatures,
        attachments: s.attachments,
        defaults: s.defaults,
      })
    );
  } catch {}
}

const stored = load();

export const useAccount = create((set, get) => ({
  senders: stored?.senders || [],
  cities: stored?.cities || [],
  recipients: stored?.recipients || [],
  signatures: stored?.signatures || [],
  attachments: stored?.attachments || [],
  // Last-used entry id per kind.
  defaults: stored?.defaults || {
    senders: null,
    cities: null,
    recipients: null,
    signatures: null,
    attachments: null,
  },

  // Add an item to a collection; it becomes the new default.
  addItem: (kind, item) =>
    set((s) => {
      const entry = { id: crypto.randomUUID(), ...item };
      const list = [entry, ...s[kind]];
      const defaults = { ...s.defaults, [kind]: entry.id };
      const next = { ...s, [kind]: list, defaults };
      persist(next);
      return { [kind]: list, defaults };
    }),

  updateItem: (kind, id, patch) =>
    set((s) => {
      const list = s[kind].map((it) => (it.id === id ? { ...it, ...patch } : it));
      const next = { ...s, [kind]: list };
      persist(next);
      return { [kind]: list };
    }),

  deleteItem: (kind, id) =>
    set((s) => {
      const list = s[kind].filter((it) => it.id !== id);
      const defaults =
        s.defaults[kind] === id ? { ...s.defaults, [kind]: list[0]?.id || null } : s.defaults;
      const next = { ...s, [kind]: list, defaults };
      persist(next);
      return { [kind]: list, defaults };
    }),

  // Mark an entry as the last-used default for its kind.
  setDefault: (kind, id) =>
    set((s) => {
      const defaults = { ...s.defaults, [kind]: id };
      persist({ ...s, defaults });
      return { defaults };
    }),

  // Move an item up or down within its collection.
  moveItem: (kind, id, dir) =>
    set((s) => {
      const list = [...s[kind]];
      const i = list.findIndex((x) => x.id === id);
      if (i < 0) return {};
      const j = dir === 'up' ? i - 1 : i + 1;
      if (j < 0 || j >= list.length) return {};
      [list[i], list[j]] = [list[j], list[i]];
      persist({ ...s, [kind]: list });
      return { [kind]: list };
    }),

  // Convenience: the default item object of a kind (or null).
  getDefault: (kind) => {
    const s = get();
    return s[kind].find((it) => it.id === s.defaults[kind]) || null;
  },
}));
